import { Box, useTheme } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { radii, statusDot, statusSurface, status as statusTokens } from '@/theme/tokens';
import { TruncatedText } from './cells';
import { TRACKING_STATUS_TONES } from './trackingTones';

export type StatusTone = keyof typeof statusTokens;

/**
 * Every backend enum maps onto six tones. The chip is tinted as drawn
 * (Components board, confirmed 2026-10-21): the tone's own fill, a 7px dot,
 * and the word in the tone's ink at 600 — never a side stripe. The owner's
 * "no tinted pill" rule is about **selection** (a nav item, a menu row, a
 * tab), which is the neutral fill; a status is not a selection.
 */
const TONES: Record<string, StatusTone> = {
  DRAFT: 'neutral',
  PENDING_REVIEW: 'info',
  MORE_INFO_REQUESTED: 'warning',
  ACTIVE: 'active',
  SUSPENDED: 'warning',
  REJECTED: 'danger',
  CLOSED: 'neutral',
  PENDING_VERIFICATION: 'pending',
  // An account waiting for its invitee: nothing is wrong, something is owed.
  INVITED: 'info',
  PENDING: 'pending',
  APPROVED: 'active',
  NOT_REQUIRED: 'neutral',
  COMPLIANT: 'active',
  EXPIRING_SOON: 'warning',
  EXPIRED: 'danger',
  MISSING: 'danger',
  COMPLETED: 'active',
  CANCELLED: 'neutral',
  ISSUED: 'info',
  PARTIALLY_DISPENSED: 'warning',
  DISPENSED: 'active',
  // Delivery lifecycle: waiting, committed, moving, done, not done, dropped.
  REQUESTED: 'pending',
  ASSIGNED: 'info',
  PICKED_UP: 'info',
  IN_TRANSIT: 'warning',
  DELIVERED: 'active',
  FAILED: 'danger',
  // Fleet.
  INACTIVE: 'neutral',
  IN_SERVICE: 'warning',
  RETIRED: 'neutral',
};

/**
 * The same word does not always mean the same thing. `EXPIRED` on a licence is
 * a compliance failure and is red; `EXPIRED` on a prescription is neither an
 * error nor a success — the window for collecting it simply closed, exactly
 * like a cancelled one, and the clinic reissues. Tones are looked up here
 * first, by namespace, and fall through to the shared table.
 */
const TONES_BY_NAMESPACE: Record<string, Record<string, StatusTone>> = {
  prescriptionStatus: { EXPIRED: 'neutral' },
  trackingStatus: TRACKING_STATUS_TONES,
  /*
   * An external register's import run. `PARTIAL` is **neither success nor
   * failure** — an hour-long crawl that stopped with its rows kept and will
   * resume — so it is `info`, not a warning and certainly not red. `REFUSED`
   * is the plausibility guard deliberately keeping the stored data, which is
   * something to look at but not a breakage.
   */
  /*
   * An invoice reading. `QUEUED` is waiting its turn and `RUNNING` is being
   * read — both are "nothing is wrong, something is owed", the same tone as an
   * invitation nobody has accepted yet. `FAILED` here means one document was
   * not read, which the user recovers from by typing the items, so it is a
   * warning rather than the red a delivery failure earns.
   */
  extractionStatus: {
    QUEUED: 'pending',
    RUNNING: 'info',
    SUCCEEDED: 'active',
    FAILED: 'warning',
  },
  importOutcome: {
    RUNNING: 'pending',
    SUCCEEDED: 'active',
    PARTIAL: 'info',
    REFUSED: 'warning',
    FAILED: 'danger',
  },
};

interface StatusChipProps {
  /** Raw backend enum value, e.g. `PENDING_REVIEW`. */
  value?: string | null;
  /** i18n namespace holding the labels, e.g. `orgStatus`. */
  namespace: string;
  tone?: StatusTone;
  size?: 'small' | 'medium';
  /**
   * The words, when they are not the enum's own label — a count on a tile,
   * "7 awaiting review". The tone still comes from `value`.
   */
  label?: string;
}

export function StatusChip({ value, namespace, tone, size = 'medium', label }: StatusChipProps) {
  const theme = useTheme();
  const { t } = useTranslation();
  if (!value) return null;

  const resolved = tone ?? TONES_BY_NAMESPACE[namespace]?.[value] ?? TONES[value] ?? 'neutral';
  const mode = theme.palette.mode;
  const color = statusTokens[resolved][mode];

  return (
    <Box
      component="span"
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 0.75,
        height: size === 'small' ? 22 : 26,
        px: size === 'small' ? 0.875 : 1.25,
        borderRadius: `${radii.md}px`,
        maxWidth: '100%',
        backgroundColor: statusSurface[resolved][mode],
      }}
    >
      <Box
        aria-hidden
        sx={{
          width: 7,
          height: 7,
          borderRadius: '50%',
          backgroundColor: statusDot[resolved][mode],
          flexShrink: 0,
        }}
      />
      {/* In a narrow column the word is cut, and then says itself in a tooltip. */}
      <TruncatedText
        sx={{ fontSize: size === 'small' ? '0.75rem' : '0.8125rem', fontWeight: 600, color }}
      >
        {label ?? t(`${namespace}.${value}`, { defaultValue: value })}
      </TruncatedText>
    </Box>
  );
}
